import {
  IonDatetime,
  IonInput,
  IonItem,
  IonLabel,
  IonSelect,
  IonSelectOption,
} from '@ionic/react';
import React from 'react';
import { useForm } from 'react-hook-form';

import moment from 'moment';

import { useEnter } from '../../../hooks/Utils/useEnter';
import { User as UserType, User_Defaults } from '../../../types/User/types';
import { mustBeAdult } from '../../../utils/date';
import { capitalizeEveryWord } from '../../../utils/string';
import { validations } from '../../../utils/validations';
import Button from '../../UI/Button';
import FormItem from '../../UI/FormItem';
import './Personal.scss';
import { PersonalProps } from './types';

const Personal: React.FC<PersonalProps> = ({
  user,
  setUser,
  goForward,
  isLoading = false,
}) => {
  const { ref, handleEnter } = useEnter();
  const normalizedUser: UserType = {
    ...User_Defaults,
    ...user,
    fecha_nacimiento: user?.fecha_nacimiento
      ? moment(user.fecha_nacimiento).format('YYYY-MM-DD')
      : '',
  };

  const { control, handleSubmit, errors } = useForm<UserType>({
    defaultValues: normalizedUser,
  });

  const onSubmit = (values: UserType) => {
    setUser({ ...normalizedUser, ...values });
    goForward();
  };

  const fields = [
    {
      name: 'nombre',
      label: 'Nombre',
      type: 'text',
      rules: { required: 'Por favor, ingrese su nombre', pattern: validations.name },
      transform: capitalizeEveryWord,
    },
    {
      name: 'apellido',
      label: 'Apellido',
      type: 'text',
      rules: { required: 'Por favor, ingrese su apellido', pattern: validations.name },
      transform: capitalizeEveryWord,
    },
    {
      name: 'dni',
      label: 'DNI',
      type: 'number',
      rules: {
        required: 'Por favor, ingrese su número de documento',
        pattern: validations.dni,
      },
    },
    {
      name: 'fecha_nacimiento',
      label: 'Fecha de nacimiento',
      type: 'date',
      rules: { required: 'Por favor, ingrese su fecha de nacimiento' },
      max: mustBeAdult,
    },
    {
      name: 'genero',
      label: 'Género',
      type: 'select',
      options: [
        { label: 'Femenino', value: 'F' },
        { label: 'Masculino', value: 'M' },
      ],
      rules: { required: 'Por favor, seleccione su género' },
    },
    {
      name: 'tel_celular',
      label: 'Teléfono',
      type: 'number',
      rules: {
        required: 'Por favor, ingrese su número de celular',
        pattern: validations.phone,
      },
    },
  ];

  return (
    <form onSubmit={handleSubmit(onSubmit)} onKeyUp={handleEnter}>
      {fields.map((field) => (
        <FormItem
          key={field.name}
          name={field.name as keyof UserType}
          control={control}
          errors={errors}
          rules={field.rules}
          loading={isLoading}
          render={({ value, onChange }) => {
            switch (field.type) {
              case 'text':
                return (
                  <IonItem>
                    <IonLabel position="floating">{field.label}</IonLabel>
                    <IonInput
                      value={value ?? ''}
                      onIonChange={(e) => onChange(e.detail.value ?? '')}
                      onIonBlur={() => {
                        if (field.transform && value) {
                          onChange(field.transform(value));
                        }
                      }}
                    />
                  </IonItem>
                );
              case 'number':
                return (
                  <IonItem>
                    <IonLabel position="floating">{field.label}</IonLabel>
                    <IonInput
                      value={value ?? ''}
                      inputMode="tel"
                      onIonChange={(e) => onChange(e.detail.value ?? '')}
                    />
                  </IonItem>
                );
              case 'date':
                return (
                  <IonItem>
                    <IonLabel position="floating">{field.label}</IonLabel>
                    <IonDatetime
                      value={value}
                      onIonChange={(e) => onChange(e.detail.value)}
                      max={field.max}
                      displayFormat="DD/MM/YYYY"
                      doneText="Aceptar"
                      cancelText="Cancelar"
                    />
                  </IonItem>
                );
              case 'select':
                return (
                  <IonItem>
                    <IonLabel>{field.label}</IonLabel>
                    <IonSelect
                      value={value}
                      placeholder="Seleccione"
                      onIonChange={(e) => onChange(e.detail.value)}
                    >
                      {field.options!.map((opt) => (
                        <IonSelectOption key={opt.value} value={opt.value}>
                          {opt.label}
                        </IonSelectOption>
                      ))}
                    </IonSelect>
                  </IonItem>
                );
              case 'email':
                return (
                  <IonItem>
                    <IonInput
                      type="email"
                      placeholder="correo.electronico@fielpet.com.ar"
                      value={value}
                      onIonChange={(e) => onChange(e.detail.value)}
                    />
                  </IonItem>
                );
              default:
                return <></>;
            }
          }}
        />
      ))}

      <Button ref={ref} type="submit">
        Siguiente
      </Button>
    </form>
  );
};

export default React.memo(Personal);
